import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Download, FileText, Loader2, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const ALL_ROLES = [
  { key: "super_admin", label: "Super Administrateur" },
  { key: "founder", label: "Fondateur" },
  { key: "director", label: "Directeur Général" },
  { key: "admin", label: "Administration" },
  { key: "scolarite", label: "Responsable Scolarité" },
  { key: "finance", label: "Service Financier" },
  { key: "teacher", label: "Enseignant" },
  { key: "parent", label: "Parent" },
  { key: "student", label: "Élève" },
];

async function downloadPdf(url, filename) {
  const resp = await api.get(url, { responseType: "blob" });
  const blob = new Blob([resp.data], { type: "application/pdf" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

export default function UserManualPage() {
  const { user } = useAuth();
  const canDownloadAll = ["super_admin", "admin", "founder"].includes(user?.role);
  const [loading, setLoading] = useState(null);

  const downloadMine = async () => {
    setLoading("mine");
    try {
      await downloadPdf("/manual/download", `Manuel_MAZID_${user?.role || "moi"}.pdf`);
      toast.success("Manuel téléchargé");
    } catch (e) { toast.error("Erreur"); }
    finally { setLoading(null); }
  };

  const downloadRole = async (role) => {
    setLoading(role);
    try {
      await downloadPdf(`/manual/download/${role}`, `Manuel_MAZID_${role}.pdf`);
      toast.success(`Manuel ${role} téléchargé`);
    } catch (e) { toast.error("Erreur"); }
    finally { setLoading(null); }
  };

  const downloadGuide = async () => {
    setLoading("guide");
    try {
      await downloadPdf("/guide/complete", "Guide_Complet_MAZID_SCHOOL_CONNECT.pdf");
      toast.success("Guide complet téléchargé");
    } catch (e) { toast.error("Erreur"); }
    finally { setLoading(null); }
  };

  const downloadFinanceGuide = async () => {
    setLoading("finance");
    try {
      await downloadPdf("/guide/finance", "Guide_Finance_Comptabilite_MAZID.pdf");
      toast.success("Guide Finance & Comptabilité téléchargé");
    } catch (e) { toast.error("Erreur"); }
    finally { setLoading(null); }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="user-manual-page">
      <div>
        <div className="chip mb-2"><BookOpen className="h-3 w-3" /><span>DOCUMENTATION</span></div>
        <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Manuel d&apos;utilisation</h1>
        <p className="text-slate-500 mt-1">Manuel PDF personnalisé selon votre rôle, avec logo école et signature autorisée.</p>
      </div>

      {/* Guide complet du site — réservé Fondateur & Super Admin */}
      {["super_admin", "founder"].includes(user?.role) && (
      <div className="bg-gradient-to-br from-emerald-50 to-green-100 border-2 border-emerald-200 rounded-2xl p-6" data-testid="complete-guide-card">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-white shadow flex items-center justify-center">
            <BookOpen className="h-7 w-7 text-[#166534]" />
          </div>
          <div className="flex-1">
            <div className="font-heading font-bold text-xl text-[#166534]">Manuel complet du site <span className="text-xs font-sans font-semibold text-emerald-700 bg-emerald-100 border border-emerald-300 rounded-full px-2 py-0.5 ml-1">Réservé Direction</span></div>
            <div className="text-sm text-slate-600">Documentation technique et fonctionnelle de toute la plateforme + dépannage « En cas de souci ». Accessible uniquement au Fondateur et au Super Administrateur.</div>
          </div>
          <Button onClick={downloadGuide} disabled={loading === "guide"} className="bg-[#166534] hover:bg-[#14532d] text-white gap-2" data-testid="dl-complete-guide">
            {loading === "guide" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4" />Télécharger PDF</>}
          </Button>
        </div>
      </div>
      )}

      {/* Guide Finance & Comptabilité — personnel autorisé */}
      {["super_admin", "founder", "admin", "director", "finance", "scolarite", "staff"].includes(user?.role) && (
      <div className="bg-gradient-to-br from-amber-50 to-orange-100 border-2 border-orange-200 rounded-2xl p-6" data-testid="finance-guide-card">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-white shadow flex items-center justify-center">
            <FileText className="h-7 w-7 text-[#EA580C]" />
          </div>
          <div className="flex-1">
            <div className="font-heading font-bold text-xl text-[#EA580C]">Guide Finance &amp; Comptabilité</div>
            <div className="text-sm text-slate-600">Manuel pas à pas pour débutants : encaissements scolarité, reçus bancaires, écritures SYSCOHADA avec exemples chiffrés, paie complète (configuration, run mensuel, workflow de validation) et paie auto depuis le pointage.</div>
          </div>
          <Button onClick={downloadFinanceGuide} disabled={loading === "finance"} className="bg-[#EA580C] hover:bg-[#c2410c] text-white gap-2" data-testid="dl-finance-guide">
            {loading === "finance" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4" />Télécharger PDF</>}
          </Button>
        </div>
      </div>
      )}

      {/* My manual */}
      <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200 rounded-2xl p-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-white shadow flex items-center justify-center">
            <FileText className="h-7 w-7 text-[#1E3A8A]" />
          </div>
          <div className="flex-1">
            <div className="font-heading font-bold text-xl text-[#1E3A8A]">Mon manuel personnel</div>
            <div className="text-sm text-slate-600">Généré pour votre profil : {user?.role}</div>
          </div>
          <Button onClick={downloadMine} disabled={loading === "mine"} className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white gap-2" data-testid="dl-mine">
            {loading === "mine" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4" />Télécharger PDF</>}
          </Button>
        </div>
      </div>

      {/* Diffuse all */}
      {canDownloadAll && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <div className="font-heading font-bold text-[#1E3A8A] mb-3">📚 Diffuser à tous les rôles</div>
          <p className="text-sm text-slate-500 mb-4">Télécharger les manuels pour chaque profil (à distribuer aux utilisateurs concernés).</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {ALL_ROLES.map(r => (
              <Button key={r.key}
                variant="outline"
                onClick={() => downloadRole(r.key)}
                disabled={loading === r.key}
                className="justify-between h-auto py-3 text-left"
                data-testid={`dl-${r.key}`}
              >
                <span>
                  <div className="font-semibold text-sm">{r.label}</div>
                  <div className="text-xs text-slate-500 uppercase">{r.key}</div>
                </span>
                {loading === r.key ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
